<script setup lang="ts">
import TaxiOfferCard from '../TaxiOfferCard.vue';
import NewTripCard from '../TripCard/TripCard.vue';
// eslint-disable-next-line no-restricted-imports
import PriceActions from '../../price-actions/PriceActions.vue';
import CardSection from '@components/ui/hotel&view/card-section/sections/CardSection.vue';
import TripCard from '@components/ui/hotelPage&view/cards/trip/TripCard.vue';
import type TripPackage from '@itinerary/trip/TripPackage';
import { extraInfoIconMapper } from '@itinerary/types';

const {
    list = [],
    title,
    displayPriceTrack,
    displayTaxiOffer,
} = defineProps<IProps>();
const emit = defineEmits<{
    buttonClicked: [item: TripPackage, index: number];
    viewSection: [];
}>();
interface IProps {
    title?: string;
    list?: TripPackage[];
    displayBanners?: boolean;
    displayPriceTrack?: boolean;
    displayTaxiOffer?: boolean;
    realTimeLoading?: boolean;
}
const currentShowDetailsIndex = ref<number>();
const priceTrackIndex = computed(() =>
    list.length >= 3 ? 2 : list.length < 3 ? list.length - 1 : 0
);

const { featureFlags } = storeToRefs(usePostHog());

watch(
    () => list,
    () => {
        currentShowDetailsIndex.value = undefined;
    }
);
</script>

<template>
    <CardSection
        :title
        :display-banners
        :list
        :real-time-loading
        @view-section="emit('viewSection')"
    >
        <template #card="{ item, index, mouseenter, mouseleave }">
            <NewTripCard
                v-if="featureFlags['trip-new-card']"
                :details="currentShowDetailsIndex === index"
                :trip-package="item"
                :is-open-details="currentShowDetailsIndex === index"
                @update:details="
                    val =>
                        val
                            ? (currentShowDetailsIndex = index)
                            : currentShowDetailsIndex === index
                              ? (currentShowDetailsIndex = undefined)
                              : undefined
                "
                @button-clicked="emit('buttonClicked', $event, index)"
                @mouseenter="mouseenter"
                @mouseleave="mouseleave"
            >
                <template v-if="item?.extraInfo" #extra-detail>
                    <div class="baggage-container">
                        <mr-divider
                            vertical
                            :thickness="1"
                            color="var(--blue-100)"
                            class="baggage-divider mobile-only"
                        />
                        <mr-icon
                            v-if="item?.extraInfo.icon"
                            :icon="extraInfoIconMapper[item?.extraInfo.icon]"
                            :size="{
                                mobile: 'small',
                                desktop: 'medium',
                            }"
                            color="var(--cool-gray-700)"
                        />
                        <mr-typography
                            size="small"
                            weight="500"
                            color="var(--cool-gray-700)"
                            class="baggage"
                            :dir="
                                item?.extraInfo.lang === 'en'
                                    ? 'ltr'
                                    : undefined
                            "
                        >
                            {{ item?.extraInfo.text }}
                        </mr-typography>
                    </div>
                </template>
            </NewTripCard>
            <TripCard
                v-else
                :trip-package="item"
                :is-open-details="currentShowDetailsIndex === index"
                @button-clicked="emit('buttonClicked', $event, index)"
                @show-details="() => (currentShowDetailsIndex = index)"
                @hide-details="() => (currentShowDetailsIndex = undefined)"
                @mouseenter="mouseenter"
                @mouseleave="mouseleave"
            />
        </template>
        <template #extra-item>
            <slot name="extra-item" />
            <PriceActions
                v-if="displayPriceTrack"
                key="forecastTrack"
                :style="{ order: priceTrackIndex }"
                mobile-only
            />
            <TaxiOfferCard v-if="displayTaxiOffer" key="taxi-offer-card" />
        </template>
    </CardSection>
</template>

<style scoped lang="scss">
.baggage-container {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 0;
    .baggage-divider {
        height: 24px;
    }
    @include desktop-only {
        align-items: flex-start;
        height: 48px;
        padding: 4px 0;
    }
}
</style>
